import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Token } from '@/types';
import { Transaction } from '@/types/transaction.types';
import { storageService } from '@/services/wallet/storage.service';

interface WalletState {
  // Wallet
  address: string;
  balance: number;
  totalBalanceUSD: number;
  totalAllNetworksUSD: number;
  isConnected: boolean;

  // Red
  selectedNetwork: string;

  // Tokens
  tokens: Token[];
  // NUEVO: tokens (con balance y precio) de TODAS las redes juntas, no solo de `selectedNetwork`.
  // Se llena desde useBalance.ts -> refreshAllNetworksBalance(), usando la data que ya
  // devuelve balanceService.refreshAllNetworksBalance(). Se usa en Dashboard/Assets
  // para mostrar los activos con balance sin importar en qué red esté seleccionada.
  allNetworksTokens: Token[];

  // Transacciones
  transactions: Transaction[];

  // UI
  isLoading: boolean;
  allNetworksLoaded: boolean;

  // Divisas y Tasas de Cambio
  selectedCurrency: string;
  exchangeRates: Record<string, number>;

  // Sincronización
  lastBalanceUpdate: number;

  // Actions
  setAddress: (address: string) => void;
  setBalance: (balance: number) => void;
  setTotalBalanceUSD: (total: number) => void;
  setTotalAllNetworksUSD: (total: number) => void;
  setIsConnected: (isConnected: boolean) => void;
  setSelectedNetwork: (network: string) => void;
  setTokens: (tokens: Token[]) => void;
  // NUEVO: setter para allNetworksTokens
  setAllNetworksTokens: (tokens: Token[]) => void;
  setTransactions: (transactions: Transaction[]) => void;
  setIsLoading: (isLoading: boolean) => void;
  setAllNetworksLoaded: (loaded: boolean) => void;
  setSelectedCurrency: (currency: string) => void;
  setExchangeRates: (rates: Record<string, number>) => void;
  setLastBalanceUpdate: (timestamp: number) => void;
  reset: () => void;
  // Limpia solo lo que depende de QUÉ wallet está activa (balances, tokens,
  // transacciones) al cambiar de wallet — a diferencia de reset(), no toca
  // selectedNetwork/selectedCurrency/exchangeRates, que son globales.
  resetForWalletSwitch: () => void;
}

const initialState = {
  address: '',
  balance: 0,
  totalBalanceUSD: 0,
  totalAllNetworksUSD: 0,
  isConnected: false,
  selectedNetwork: 'omdb',
  tokens: [],
  allNetworksTokens: [], // NUEVO — igual que `tokens`, no se persiste (ver `partialize` más abajo)
  transactions: [],
  isLoading: false,
  allNetworksLoaded: false,
  selectedCurrency: 'usd',
  exchangeRates: { usd: 1 },
  lastBalanceUpdate: 0,
};

export const useWalletStore = create<WalletState>()(
  persist(
    (set) => ({
      ...initialState,

      // Actions
      setAddress: (address) => set({ address }),
      setBalance: (balance) => set({ balance }),
      setTotalBalanceUSD: (totalBalanceUSD) => set({ totalBalanceUSD }),
      setTotalAllNetworksUSD: (totalAllNetworksUSD) => set({ totalAllNetworksUSD }),
      setIsConnected: (isConnected) => set({ isConnected }),
      
      setSelectedNetwork: (network) => {
        storageService.saveSelectedNetwork(network);
        set({ selectedNetwork: network });
      },
      
      setTokens: (tokens) => set({ tokens }),
      setAllNetworksTokens: (allNetworksTokens) => set({ allNetworksTokens }),
      setTransactions: (transactions) => set({ transactions }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setAllNetworksLoaded: (allNetworksLoaded) => set({ allNetworksLoaded }),

      setSelectedCurrency: (currency) => {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('selected_currency', currency);
        }
        set({ selectedCurrency: currency });
      },

      setExchangeRates: (exchangeRates) => set({ exchangeRates }),
      setLastBalanceUpdate: (timestamp) => set({ lastBalanceUpdate: timestamp }),
      reset: () => set(initialState),
      resetForWalletSwitch: () =>
        set({
          balance: 0,
          totalBalanceUSD: 0,
          totalAllNetworksUSD: 0,
          tokens: [],
          allNetworksTokens: [],
          transactions: [],
          allNetworksLoaded: false,
          lastBalanceUpdate: 0,
        }),
    }),
    {
      name: 'omd-wallet-state',
      storage: createJSONStorage(() => localStorage),
      partialize: (state): any => ({ 
        selectedNetwork: state.selectedNetwork,
        selectedCurrency: state.selectedCurrency,
        address: state.address,
        isConnected: state.isConnected
      }),
    }
  )
);

export default useWalletStore;
